/**
 * Zod schemas for source-group domain — GH-235.
 *
 * Exports:
 *   sourceGroupSchema             — enum schema for the 7 canonical groups
 *   sourceGroupStateSchema        — enum schema for the 4 per-group states
 *   sourceGroupCompletenessSchema — validated shape for a single group's completeness record
 *   packageCompletenessSchema     — validated shape for a full package completeness response
 *
 * Design notes:
 *   - packageCompletenessSchema uses a superRefine to enforce the invariant:
 *     score === null iff expectedGroups === 0
 *   - All count fields (batchCount, itemCount, etc.) are non-negative integers.
 *   - computedAt is a unix millisecond integer.
 *   - blockers is an array of SourceGroup values (subset of SOURCE_GROUPS).
 */

import { z } from 'zod'
import { SOURCE_GROUPS, SOURCE_GROUP_STATE_VALUES } from '../constants'

// ── Enum schemas ───────────────────────────────────────────────────────────────

/**
 * Validates a source-group string against the 7 canonical values.
 * Unknown strings are rejected (use 'other' for unrecognized groups).
 */
export const sourceGroupSchema = z.enum(SOURCE_GROUPS)

/**
 * Validates a per-group processing state.
 * Values: missing | partial | complete | failed
 */
export const sourceGroupStateSchema = z.enum(SOURCE_GROUP_STATE_VALUES)

// ── Per-group completeness schema ──────────────────────────────────────────────

/**
 * Validated shape for a single source group's completeness breakdown.
 * All counts are non-negative integers; failedCount tracks terminal-failure items.
 */
export const sourceGroupCompletenessSchema = z.object({
  group: sourceGroupSchema,
  state: sourceGroupStateSchema,
  /** Number of batches tagged with this source group. */
  batchCount: z.number().int().min(0),
  /** Total ingest items across all tagged batches. */
  itemCount: z.number().int().min(0),
  /** Items with status='accepted' (terminal-success per GH-235 locked decision §2). */
  acceptedCount: z.number().int().min(0),
  /** Items with status='failed-*' or status='rejected'. */
  failedCount: z.number().int().min(0),
})

export type SourceGroupCompletenessSchema = z.infer<typeof sourceGroupCompletenessSchema>

// ── Package completeness schema ────────────────────────────────────────────────

/**
 * Validated wire shape for a full package completeness response.
 *
 * Invariant (enforced via superRefine):
 *   score === null  iff  expectedGroups === 0
 *   score ∈ [0,1]   when expectedGroups > 0
 */
export const packageCompletenessSchema = z.object({
  packageId: z.string(),
  /**
   * Completeness ratio. Must be null when expectedGroups=0.
   * Must be a number in [0,1] when expectedGroups > 0.
   */
  score: z.number().min(0).max(1).nullable(),
  /** Count of expected source groups; must be ≥ 0. */
  expectedGroups: z.number().int().min(0),
  /** Count of groups in 'complete' state; must be ≥ 0. */
  completeGroups: z.number().int().min(0),
  /** Per-group breakdown, ordered by SOURCE_GROUPS canonical ordering. */
  groups: z.array(sourceGroupCompletenessSchema),
  /** Source groups in 'missing' or 'failed' state — operational blockers. */
  blockers: z.array(sourceGroupSchema),
  /** Unix ms — when this record was computed. */
  computedAt: z.number().int(),
}).superRefine((val, ctx) => {
  // Enforce: score === null iff expectedGroups === 0
  if (val.expectedGroups === 0 && val.score !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['score'],
      message: 'score must be null when expectedGroups is 0',
    })
  }
  if (val.expectedGroups > 0 && val.score === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['score'],
      message: 'score must not be null when expectedGroups > 0',
    })
  }
})

export type PackageCompletenessSchema = z.infer<typeof packageCompletenessSchema>
